import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Save, AlertTriangle, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, apiError, formatDateUK } from "@/lib/api";
import DocumentUpload from "@/components/DocumentUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const KINDS = [
  { id: "supplier_payment", label: "Pay a supplier" },
  { id: "customer_collection", label: "Customer paid us" },
  { id: "account_sale", label: "Customer credit sale" },
  { id: "cash_deposit", label: "Cash into bank" },
  { id: "cash_withdrawal", label: "Cash taken out" },
  { id: "other_cash_receipt", label: "Other cash in" },
];
const METHODS = [["cash", "Cash"], ["bank_transfer", "Bank transfer"], ["card", "Card"]];
const today = () => new Date().toLocaleDateString("en-GB", { timeZone: "Europe/London" });
const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

const Pills = ({ items, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {items.map(([id, label]) => (
      <button key={id} type="button" onClick={() => onChange(id)} data-testid={`${testid}-${id}`}
        className={`min-h-[44px] px-4 rounded-full border text-sm transition-colors ${value === id ? "bg-navy text-white border-navy" : "bg-white border-navy/15 text-navy"}`}>
        {label}
      </button>
    ))}
  </div>
);

function OpenItems({ items, alloc, setAlloc, idKey, dateKey, testid }) {
  if (!items.length) return <p className="text-sm text-navy/50" data-testid={`${testid}-empty`}>Nothing outstanding.</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((i) => {
        const id = i[idKey];
        const on = alloc[id] !== undefined;
        return (
          <li key={id} className="flex items-center gap-3 border border-navy/10 rounded-lg p-3">
            <input type="checkbox" className="w-5 h-5" checked={on} data-testid={`${testid}-check-${id}`}
              onChange={() => setAlloc((a) => { const n = { ...a }; if (on) delete n[id]; else n[id] = i.outstanding; return n; })} />
            <div className="flex-1 text-sm">
              <div className="font-medium text-navy">{i.invoice_number || i.reference || `#${id}`}</div>
              <div className="text-xs text-navy/50">{formatDateUK(i[dateKey])} · owed {formatGBP(i.outstanding)}</div>
            </div>
            {on && <Input className="w-28 h-11" inputMode="decimal" value={alloc[id]} data-testid={`${testid}-amount-${id}`}
              onChange={(e) => setAlloc((a) => ({ ...a, [id]: e.target.value }))} />}
          </li>
        );
      })}
    </ul>
  );
}

function CustomerPicker({ storeId, customers, value, onChange, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    try { const r = await api.post("/customers", { store_id: storeId, name }); setName(""); await reload(); onChange(r.data.id); toast.success("Customer added"); }
    catch (e) { toast.error(apiError(e, "Could not add customer")); }
  };
  return (
    <div className="space-y-2">
      <select className="gm-input w-full h-11 rounded-lg border border-navy/15 px-3 bg-white" value={value || ""} data-testid="customer-select"
        onChange={(e) => onChange(parseInt(e.target.value) || null)}>
        <option value="">Choose customer…</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.outstanding ? ` (owes ${formatGBP(c.outstanding)})` : ""}</option>)}
      </select>
      <div className="flex gap-2">
        <Input placeholder="New customer name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="new-customer-name" />
        <button type="button" onClick={add} disabled={name.trim().length < 2} data-testid="add-customer-btn"
          className="min-h-[44px] px-3 rounded-lg border border-navy/15 text-navy disabled:opacity-40"><UserPlus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function RecordPaymentPage() {
  const navigate = useNavigate();
  const { activeStoreId, stores } = useAuth();
  const storeId = activeStoreId || stores[0]?.id;
  const [kind, setKind] = useState("supplier_payment");
  const [method, setMethod] = useState("bank_transfer");
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [docs, setDocs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [partyId, setPartyId] = useState(null);
  const [items, setItems] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  const linked = kind === "supplier_payment" || kind === "customer_collection";
  const total = linked ? r2(Object.values(alloc).reduce((s, v) => s + r2(v), 0)) : r2(amount);

  const loadCustomers = async () => { const r = await api.get(`/customers?store_id=${storeId}`); setCustomers(r.data); };
  useEffect(() => {
    if (!storeId) { return; }
    api.get(`/supplier-balances?store_id=${storeId}`).then((r) => setSuppliers(r.data)).catch(() => {});
    api.get(`/customers?store_id=${storeId}`).then((r) => setCustomers(r.data)).catch(() => {});
  }, [storeId]);
  useEffect(() => {
    setAlloc({}); setItems([]);
    if (!partyId) { return; }
    const url = kind === "supplier_payment" ? `/suppliers/${partyId}/open-invoices?store_id=${storeId}` : `/customers/${partyId}/ledger`;
    api.get(url).then((r) => setItems(kind === "supplier_payment" ? r.data.filter((i) => i.outstanding > 0) : r.data.open_sales)).catch(() => {});
  }, [partyId, kind, storeId]);
  useEffect(() => {
    setPartyId(null); setConfirmed(false); setDupWarn(null);
    if (kind === "cash_deposit" || kind === "cash_withdrawal") { setMethod("cash"); }
  }, [kind]);

  const save = async (submit, ack = false) => {
    setBusy(true);
    try {
      if (kind === "account_sale") {
        await api.post("/account-sales", { store_id: storeId, customer_id: partyId, sale_date: date, amount: total, reference, notes });
        toast.success("Credit sale recorded against the customer");
      } else {
        const key = kind === "supplier_payment" ? "purchase_id" : "account_sale_id";
        await api.post("/payments", {
          store_id: storeId, kind, method, amount: total, movement_date: date, reference, notes, submit, confirmed: submit,
          acknowledge_possible_duplicate: ack, document_ids: docs.map((d) => d.id),
          supplier_id: kind === "supplier_payment" ? partyId : null, customer_id: kind === "customer_collection" ? partyId : null,
          allocations: linked ? Object.entries(alloc).map(([id, a]) => ({ [key]: parseInt(id), amount: r2(a) })) : [],
        });
        toast.success(submit ? "Payment recorded" : "Draft saved — it does not reduce any balance until recorded");
      }
      navigate("/payments");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "possible_duplicate_payment") setDupWarn({ message: d.message, submit });
      else toast.error(apiError(e, "Could not save"));
    } finally { setBusy(false); }
  };

  const needsParty = linked || kind === "account_sale";
  const ready = total > 0 && (!needsParty || partyId) && (!linked || Object.keys(alloc).length > 0);
  return (
    <div className="space-y-5 pb-4" data-testid="record-payment-page">
      <button type="button" onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-navy/60 min-h-[44px]" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="text-2xl font-bold text-navy">Payments & cash</h1>
      <Pills items={KINDS.map((k) => [k.id, k.label])} value={kind} onChange={setKind} testid="kind" />
      {kind === "account_sale" && <p className="text-xs text-navy/60" data-testid="account-sale-note">This records who owes the money. It is not extra sales: the Z report already counts it as account sales.</p>}
      {kind === "customer_collection" && <p className="text-xs text-navy/60" data-testid="collection-note">Money received for an earlier credit sale. It reduces what the customer owes and is not a new sale.</p>}
      <Card className="border-navy/5 shadow-none"><CardContent className="p-4 space-y-4">
        {kind === "supplier_payment" && (
          <div className="space-y-2"><Label>Supplier</Label>
            <select className="w-full h-11 rounded-lg border border-navy/15 px-3 bg-white" value={partyId || ""} data-testid="supplier-select"
              onChange={(e) => setPartyId(parseInt(e.target.value) || null)}>
              <option value="">Choose supplier…</option>
              {suppliers.filter((s) => s.outstanding > 0).map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier} (owed {formatGBP(s.outstanding)})</option>)}
            </select></div>
        )}
        {(kind === "customer_collection" || kind === "account_sale") && (
          <div className="space-y-2"><Label>Customer</Label><CustomerPicker storeId={storeId} customers={customers} value={partyId} onChange={setPartyId} reload={loadCustomers} /></div>
        )}
        {linked && partyId && (
          <div className="space-y-2"><Label>{kind === "supplier_payment" ? "Invoices this pays" : "Credit sales this pays"}</Label>
            <OpenItems items={items} alloc={alloc} setAlloc={setAlloc} idKey={kind === "supplier_payment" ? "purchase_id" : "account_sale_id"}
              dateKey={kind === "supplier_payment" ? "invoice_date" : "sale_date"} testid="open-items" /></div>
        )}
        {!linked && <div className="space-y-2"><Label>Amount (£)</Label><Input inputMode="decimal" className="h-11" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-input" /></div>}
        {kind !== "account_sale" && <div className="space-y-2"><Label>Paid by</Label>
          <Pills items={kind === "cash_deposit" || kind === "cash_withdrawal" ? METHODS.slice(0, 1) : METHODS} value={method} onChange={setMethod} testid="method" /></div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Date</Label><Input className="h-11" value={date} onChange={(e) => setDate(e.target.value)} placeholder="DD/MM/YYYY" data-testid="date-input" /></div>
          <div className="space-y-2"><Label>Reference (optional)</Label><Input className="h-11" value={reference} onChange={(e) => setReference(e.target.value)} data-testid="reference-input" /></div>
        </div>
        {kind !== "account_sale" && (
          <div className="space-y-2"><Label>Receipt or bank confirmation (optional)</Label>
            <DocumentUpload kind="receipt" storeId={storeId} onUpload={(d) => setDocs((p) => [...p, d])} onRemove={(id) => setDocs((p) => p.filter((x) => x.id !== id))} /></div>
        )}
        <Input className="h-11" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input" />
      </CardContent></Card>
      <div className="sticky bottom-0 bg-cream pt-2 space-y-3" data-testid="payment-action-bar">
        <div className="flex justify-between text-navy"><span>Total</span><strong data-testid="payment-total">{formatGBP(total)}</strong></div>
        {dupWarn && (
          <div className="rounded-lg bg-amber-50 text-amber-800 text-sm p-3 space-y-2" data-testid="duplicate-payment-warning">
            <div className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{dupWarn.message}</div>
            <button type="button" className="underline min-h-[44px] disabled:opacity-40" disabled={dupWarn.submit && !confirmed}
              onClick={() => save(dupWarn.submit, true)} data-testid="record-anyway-btn">This is a separate payment — record it</button>
          </div>
        )}
        {kind !== "account_sale" && (
          <label className="flex items-center gap-2 text-sm text-navy min-h-[44px]"><input type="checkbox" className="w-5 h-5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="confirm-payment-checkbox" />I confirm these details are correct</label>
        )}
        <div className="grid grid-cols-2 gap-2">
          {kind !== "account_sale" && <button type="button" disabled={!ready || busy} onClick={() => save(false)} data-testid="save-payment-draft-btn"
            className="min-h-[48px] rounded-xl border border-navy/15 text-navy flex items-center justify-center gap-2 disabled:opacity-40"><Save className="w-4 h-4" />Draft</button>}
          <button type="button" disabled={!ready || busy || (kind !== "account_sale" && !confirmed)} onClick={() => save(true)} data-testid="submit-payment-btn"
            className={`min-h-[48px] rounded-xl bg-gold text-navy font-semibold flex items-center justify-center gap-2 disabled:opacity-40 ${kind === "account_sale" ? "col-span-2" : ""}`}><Send className="w-4 h-4" />{kind === "account_sale" ? "Save credit sale" : "Record payment"}</button>
        </div>
      </div>
    </div>
  );
}
